import React, { useState, useEffect, useRef } from 'react';
import { X, Send, MessageCircle, Minimize2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Load session from localStorage
    const savedSession = localStorage.getItem('chatbot_session_id');
    if (savedSession) {
      setSessionId(savedSession);
      loadChatHistory(savedSession);
    }
  }, []);

  const loadChatHistory = async (sid) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/chat/history/${sid}`);
      const data = await response.json();
      if (data.messages && data.messages.length > 0) {
        setMessages(data.messages.map(msg => ({
          role: msg.role,
          content: msg.content,
          timestamp: msg.created_at
        })));
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    
    // Add user message to UI immediately
    const newUserMsg = {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, newUserMsg]);
    setIsLoading(true);

    try {
      const cartSessionId = localStorage.getItem('sessionId');
      const response = await fetch(`${BACKEND_URL}/api/chat/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId,
          cart_session_id: cartSessionId || null
        })
      });

      const data = await response.json();
      
      // Save session ID
      if (data.session_id && !sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem('chatbot_session_id', data.session_id);
      }

      // Add assistant message — include checkout flag so the message can render the CTA button.
      const assistantMsg = {
        role: 'assistant',
        content: data.message,
        timestamp: data.timestamp,
        checkout_cta: !!data.checkout_cta,
      };
      setMessages(prev => [...prev, assistantMsg]);

      // If the bot mutated the cart (added or removed), broadcast so the
      // navbar counter refreshes.
      const added = Array.isArray(data.added_items) ? data.added_items : [];
      const removed = Array.isArray(data.removed_items) ? data.removed_items : [];
      if (data.cart_updated && (added.length > 0 || removed.length > 0)) {
        window.dispatchEvent(new CustomEvent('cart-updated'));
        const sysLines = [];
        if (added.length > 0) {
          sysLines.push(`✅ Añadido: ${added.map(i => `${i.quantity}× ${i.product_name}`).join(', ')}`);
        }
        if (removed.length > 0) {
          sysLines.push(`🗑️ Quitado: ${removed.map(i => `${i.quantity}× ${i.product_name}`).join(', ')}`);
        }
        if (sysLines.length > 0) {
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: sysLines.join('\n'),
            timestamp: new Date().toISOString(),
            _system: true,
          }]);
        }
      }
      
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMsg = {
        role: 'assistant',
        content: 'Lo siento, ha ocurrido un error. Por favor, inténtalo de nuevo o contacta con nosotros por WhatsApp: +34 649 47 18 89',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
    setIsMinimized(false);
    
    // Add welcome message if no messages yet
    if (!isOpen && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: 'Bienvenido a ARTHUR FITZ. Soy su asistente virtual. ¿En qué puedo ayudarle hoy?',
        timestamp: new Date().toISOString()
      }]);
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={toggleChat}
        className="fixed bottom-6 right-6 z-50 bg-black text-white p-4 rounded-full shadow-lg hover:bg-gray-800 transition-colors"
        aria-label="Abrir chat"
      >
        <MessageCircle size={24} />
      </button>
    );
  }

  return (
    <div 
      className={`fixed bottom-6 right-6 z-50 bg-white border-2 border-black shadow-2xl transition-all ${
        isMinimized ? 'h-14' : 'h-[600px]'
      } w-[400px] flex flex-col`}
    >
      {/* Header */}
      <div className="bg-black text-white p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageCircle size={20} />
          <span className="font-medium">Asistente ARTHUR FITZ</span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="hover:bg-gray-800 p-1 rounded transition-colors"
            aria-label="Minimizar"
          >
            <Minimize2 size={18} />
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="hover:bg-gray-800 p-1 rounded transition-colors"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-lg ${
                    msg.role === 'user'
                      ? 'bg-black text-white'
                      : 'bg-white text-gray-900 border border-gray-200'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  {msg.checkout_cta && (
                    <a
                      href="/checkout"
                      className="mt-3 inline-block bg-black text-white text-sm px-4 py-2 rounded hover:bg-gray-800 transition-colors tracking-wider"
                      data-testid="chat-checkout-cta"
                    >
                      Ir a pagar →
                    </a>
                  )}
                  <span className="text-xs opacity-70 mt-1 block">
                    {new Date(msg.timestamp).toLocaleTimeString('es-ES', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </span>
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-900 border border-gray-200 p-3 rounded-lg">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-gray-200 bg-white">
            <div className="flex gap-2">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Escribe tu mensaje..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-black text-sm"
                disabled={isLoading}
              />
              <button
                onClick={sendMessage}
                disabled={isLoading || !inputMessage.trim()}
                className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                aria-label="Enviar mensaje"
              >
                <Send size={18} />
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              Horario de atención: L-V 9:00-19:00
            </p>
          </div>
        </>
      )}
    </div>
  );
}
